// Keeps the parts of a person the selfie segmenter loses: a bare arm or hand,
// hair, a face.
//
// The selfie segmenter's edge cutoff sits high on purpose, to keep Adam's
// chair and the guitar behind him out (2026-10-04). It read his raised
// forearm and hand under that cutoff (2026-10-14: "it doesn't recognize hands
// or arms at all"), and a forearm close to the camera with his face out of
// frame read near zero. The pose landmarker could not help there: it finds a
// person by the face. The multiclass segmenter splits a person into hair,
// face skin, body skin, and clothes, and its skin and hair read that arm, a
// raised hand to the fingertip, and his hair. Its clothes and "other" classes
// took some of the chair, so only hair and skin are used, as a second way into
// the mask: a pixel is person if the selfie model says so past its cutoff, or
// if hair or skin does past theirs.

/** The multiclass model's masks, in its order. */
export const PART_HAIR = 1;
export const PART_BODY = 2;
export const PART_FACE = 3;

/** The strongest of hair, face skin, and body skin at each pixel, written
 * into `out`. Every mask is the same size as `out`. */
export function partsField(masks: readonly Float32Array[], out: Float32Array): boolean {
  const hair = masks[PART_HAIR];
  const face = masks[PART_FACE];
  const body = masks[PART_BODY];
  if (!hair || !face || !body || hair.length !== out.length || face.length !== out.length || body.length !== out.length) return false;
  for (let i = 0; i < out.length; i++) out[i] = Math.max(hair[i]!, face[i]!, body[i]!);
  return true;
}

function smoothstep(v: number, low: number, high: number): number {
  const t = Math.min(1, Math.max(0, (v - low) / (high - low)));
  return t * t * (3 - 2 * t);
}

/** The person alpha for one frame: the selfie confidence through `low` to
 * `high`, or the parts field through `partLow` to `partHigh`, whichever keeps
 * more. With no field it is the selfie alone. Written into `out`. */
export function partsAlpha(
  conf: Float32Array,
  field: Float32Array | null,
  low: number,
  high: number,
  partLow: number,
  partHigh: number,
  out: Float32Array,
): void {
  const useField = field !== null && field.length === conf.length;
  for (let i = 0; i < conf.length; i++) {
    let a = smoothstep(conf[i]!, low, high);
    if (useField && a < 1) {
      const b = smoothstep(field[i]!, partLow, partHigh);
      if (b > a) a = b;
    }
    out[i] = a;
  }
}
